import { Component, inject } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { decodeJwtToken } from '../../../shared/utils/jwt.util';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  currentYear = new Date().getFullYear();
  showPassword = false;
  loginForm: FormGroup;
  loading = false;
  redirectUrl: string | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.route.queryParamMap.subscribe((params) => {
      this.redirectUrl = params.get('redirect-url');
    });
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.loading = true;
      const { email, password } = this.loginForm.value;
      this.authService.initiateLogin(email, password).subscribe({
        next: (data) => {
          this.toastService.showSuccess('Login successful!');
          this.loading = false;
          const redirectUrl = this.redirectUrl ?? '';

          // Check if redirecting to admin application and user is not admin
          if (
            this.isAdminApplication(redirectUrl) &&
            !this.isUserAdmin(data.sAccessToken)
          ) {
            // Show error instead of redirecting to admin app
            this.toastService.showError(
              'Access denied. Admin privileges required to access this application.',
            );
            return;
          }

          window.location.href = `${redirectUrl}?${environment.refreshToken}=${data.sAccessToken}`;
        },
        error: (_err) => {
          this.toastService.showError('Login failed. Please try again.');
          this.loading = false;
        },
      });
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  /**
   * Checks if the redirect URL is for an admin application
   */
  private isAdminApplication(redirectUrl: string): boolean {
    return redirectUrl.includes('/admin') || redirectUrl.includes('admin.');
  }

  /**
   * Checks if the user has admin privileges based on the JWT token
   */
  private isUserAdmin(token: string): boolean {
    const decodedToken = decodeJwtToken(token);
    return decodedToken?.bIsAdmin === true;
  }
}
